import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Wordmark } from "@/components/Brand";

const EMAIL = "hello@driveassure.systems";
const Mail = () => <a href={`mailto:${EMAIL}`} className="text-[#1FA097] font-medium">{EMAIL}</a>;

const Section = ({ n, title, children }) => (
  <section className="mt-9">
    <h2 className="font-display font-bold text-xl text-[#2B2B2B]">{n}. {title}</h2>
    <div className="mt-3 space-y-3 text-[#2B2B2B]/70 leading-relaxed">{children}</div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="space-y-2 pl-1">
    {items.map((t, i) => (
      <li key={i} className="flex gap-3">
        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#2BBFB3] shrink-0" />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

export default function Terms() {
  return (
    <div data-testid="terms-page" className="min-h-screen bg-white text-[#2B2B2B]">
      <header className="border-b border-[#E5E5E5]">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-5 flex items-center justify-between">
          <Link to="/" data-testid="legal-logo-home"><Wordmark /></Link>
          <Link
            to="/"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#2B2B2B]/60 hover:text-[#2B2B2B] transition-colors"
          >
            <ArrowLeft size={16} /> Back to site
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 sm:px-8 py-16 lg:py-24">
        <p className="overline text-[#2BBFB3]">Legal</p>
        <h1 className="mt-4 font-display font-extrabold tracking-tight text-4xl sm:text-5xl">Terms of Service</h1>
        <p className="mt-4 text-[#2B2B2B]/50">Last updated: October 2026</p>

        <Section n={1} title="Agreement to these terms">
          <p>
            These Terms of Service ("Terms") govern your access to and use of the DriveAssure mobile
            application, website and related services (together, the "Service"). By creating an account,
            downloading the app or using the Service, you agree to be bound by these Terms. If you do not
            agree, you must not use the Service.
          </p>
        </Section>

        <Section n={2} title="Who we are">
          <p>
            DriveAssure is operated by James Sadler, ABN 76 728 192 915 ("DriveAssure", "we", "us", "our"),
            based in South Australia. You can contact us at <Mail />
          </p>
        </Section>

        <Section n={3} title="Eligibility and accounts">
          <Bullets items={[
            "You must be at least 16 years old, or have the consent of a parent or guardian, to create an account.",
            "You are responsible for the accuracy of the information you provide and for keeping your login credentials secure.",
            "You are responsible for all activity that occurs under your account.",
            "Professional accounts (Instructor, Founding Instructor, Pro) are for use by qualified or trainee driving instructors operating lawfully in their jurisdiction.",
          ]} />
        </Section>

        <Section n={4} title="Plans, pricing and billing">
          <p>All prices are in Australian dollars (AUD) and include GST where applicable. The Service offers the following plans:</p>
          <Bullets items={[
            "Free — for learners, supervising drivers and parents. Free to download and register, with full functionality limited to five lessons per month and PDF export to family.",
            "Founding Instructor — A$30 per month or A$300 per year (two months free). Limited to the first 30 instructors; your price is locked for the lifetime of a continuous, active subscription (see section 5).",
            "DriveAssure Instructor — A$30 per month or A$300 per year.",
            "DriveAssure Pro — A$45 per month or A$450 per year.",
          ]} />
          <p>
            Paid subscriptions are billed in advance and renew automatically each month or year until
            cancelled. Payments are processed securely by Stripe; by subscribing you authorise us (via Stripe)
            to charge your payment method on each renewal. You may cancel at any time from your account, and
            cancellation takes effect at the end of your current billing period. Except where required by law
            (including the Australian Consumer Law), payments already made are non-refundable. We may change
            plan pricing on reasonable notice; changes do not affect the current paid period, and the Founding
            Instructor price lock in section 5 continues to apply.
          </p>
        </Section>

        <Section n={5} title="Founding Instructor program">
          <p>
            The Founding Instructor program is limited to the first 30 instructors who subscribe. Founding
            members retain their subscription price for the lifetime of their subscription, provided it remains
            continuously active. If a Founding Instructor subscription lapses, is cancelled, or payment fails
            and is not remedied, the price lock and founding status may be forfeited and standard pricing will
            apply on re-subscription, subject to availability.
          </p>
        </Section>

        <Section n={6} title="Family Link and shared data">
          <p>
            Instructors may invite learners, parents and supervising drivers to connect to their school using a
            Family Link code or QR code. By linking an account, you consent to the relevant lesson records,
            progress reports and practice plans being shared between the linked instructor, learner and
            approved supervisors. Instructors are responsible for sharing Family Link codes only with
            authorised individuals and for managing who has access to a learner's information.
          </p>
        </Section>

        <Section n={7} title="Acceptable use">
          <p>You agree not to:</p>
          <Bullets items={[
            "Use the Service for any unlawful purpose or in breach of road-safety or instruction regulations in your jurisdiction;",
            "Interact with the app in a way that distracts from the safe operation of a vehicle — the driver must always remain in full control and compliant with road rules;",
            "Upload false, misleading or unlawful content, or record or share another person's data without a lawful basis;",
            "Attempt to reverse engineer, disrupt, overload or gain unauthorised access to the Service; or",
            "Resell, sublicense or commercially exploit the Service except as expressly permitted.",
          ]} />
        </Section>

        <Section n={8} title="Lesson data, GPS and accuracy">
          <p>
            The Service records route, telemetry and GPS data during active lessons and generates assessments,
            reports and AI-assisted practice plans to support instruction. These outputs are tools to assist a
            qualified instructor and are not a substitute for professional judgement, the official driving
            curriculum, or any assessment conducted by a licensing authority. GPS and telemetry accuracy may
            vary with device and environmental conditions.
          </p>
        </Section>

        <Section n={9} title="Intellectual property">
          <p>
            The Service, including its software, design, branding and content, is owned by DriveAssure and
            protected by intellectual property laws. We grant you a limited, non-exclusive, non-transferable,
            revocable licence to use the Service for its intended purpose. You retain ownership of the lesson
            content and records you create, and you grant us a licence to host, process and display that content
            as needed to provide the Service.
          </p>
        </Section>

        <Section n={10} title="Third-party services">
          <p>
            The Service relies on third parties including Stripe (payments), cloud infrastructure providers
            (hosting) and mapping/location providers. Your use of those features may be subject to the relevant
            third party's terms. We are not responsible for third-party services outside our control.
          </p>
        </Section>

        <Section n={11} title="Consumer guarantees, disclaimers and liability">
          <p>
            Nothing in these Terms excludes, restricts or modifies any consumer guarantee, right or remedy you
            have under the Australian Consumer Law or other laws that cannot lawfully be excluded. Subject to
            those rights, the Service is provided "as is" and "as available", and to the maximum extent permitted
            by law we exclude all other warranties and limit our total liability to the amount you paid us for
            the Service in the 12 months before the relevant claim. We are not liable for indirect or
            consequential loss.
          </p>
        </Section>

        <Section n={12} title="Suspension and termination">
          <p>
            You may stop using the Service and close your account at any time. We may suspend or terminate your
            access if you breach these Terms, if required by law, or to protect the Service or its users. On
            termination, your right to use the Service ends; sections intended to survive (including payment,
            intellectual property, disclaimers and liability) will continue to apply.
          </p>
        </Section>

        <Section n={13} title="Changes to these terms">
          <p>
            We may update these Terms from time to time. We will post the updated version here and update the
            "Last updated" date, and where changes are material we will take reasonable steps to notify you.
            Continued use of the Service after changes take effect constitutes acceptance of the updated Terms.
          </p>
        </Section>

        <Section n={14} title="Governing law">
          <p>
            These Terms are governed by the laws of South Australia, Australia, and you submit to the
            non-exclusive jurisdiction of the courts of that state.
          </p>
        </Section>

        <Section n={15} title="Contact">
          <p>For any questions about these Terms, contact us at <Mail /></p>
        </Section>
      </main>
    </div>
  );
}
